(function () {
  'use strict';

  // Per-container state lives on the DOM node:
  //   container._bfwtWidgetId       — id returned by turnstile.render()
  //   container._bfwtTokenResolvers — promise resolvers waiting for next token
  // This lets us call turnstile.reset(id) on failure, and ensureToken()
  // can wait for the callback to fire instead of grabbing a stale/empty value.

  function setHiddenInput(form, token) {
    let input = form.querySelector('input[name="cf-turnstile-response"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'cf-turnstile-response';
      form.appendChild(input);
    }
    input.value = token || '';
    return input;
  }

  function flushResolvers(container, token) {
    const resolvers = container._bfwtTokenResolvers || [];
    container._bfwtTokenResolvers = [];
    resolvers.forEach((fn) => {
      try {
        fn(token);
      } catch (e) {
        /* noop */
      }
    });
  }

  function initializeTurnstile() {
    if (typeof turnstile === 'undefined') {
      console.warn(
        'BFWT Turnstile: `turnstile` object not found after onload.'
      );
      return;
    }

    const turnstileContainers = document.querySelectorAll(
      '.bfwt-turnstile-container:not(.rendered)'
    );
    if (turnstileContainers.length === 0) return;

    const previousActiveElement = document.activeElement;

    turnstileContainers.forEach((container) => {
      // Cloudflare Turnstile requirement: container must be visible to calculate iframe dimensions & postMessage origin.
      // If container is inside a hidden modal, tab, or offscreen element (display: none / zero rect), defer rendering.
      const isVisible = !!(container.offsetWidth || container.offsetHeight || container.getClientRects().length);
      if (!isVisible) {
        return;
      }

      const renderOptions = {
        sitekey: container.dataset.sitekey,
        theme:
          document.documentElement.getAttribute('data-color-mode') === 'dark' ||
          (!document.documentElement.hasAttribute('data-color-mode') &&
            window.matchMedia('(prefers-color-scheme: dark)').matches)
            ? 'dark'
            : 'light',
        callback: function (token) {
          const form = container.closest('form');
          if (form) setHiddenInput(form, token);
          flushResolvers(container, token);
        },
        'error-callback': function () {
          console.warn('BFWT Turnstile: Verification callback not completed');
          flushResolvers(container, null);
        },
        'timeout-callback': function () {
          console.warn('BFWT Turnstile: Timeout — auto-resetting widget');
          // Token expired without being used; refresh silently
          if (container._bfwtWidgetId) turnstile.reset(container._bfwtWidgetId);
        },
      };

      if (container.dataset.appearance) {
        renderOptions.appearance = container.dataset.appearance;
      }

      const widgetId = turnstile.render(container, renderOptions);
      container._bfwtWidgetId = widgetId;
      container._bfwtTokenResolvers = [];
      container.classList.add('rendered');
    });

    if (previousActiveElement && previousActiveElement.isConnected) {
      setTimeout(() => previousActiveElement.focus(), 100);
    }

    document.dispatchEvent(new Event('bfwt-turnstile-ready'));
  }

  // Public API consumed by form submit handlers.
  window.bfwtTurnstile = {
    /**
     * Resolve with the form's current Turnstile token, waiting for the
     * widget callback if necessary. Resolves with null if no widget is
     * present (degrade gracefully when Turnstile is disabled).
     *
     * @param {HTMLFormElement} form
     * @param {{ timeoutMs?: number }} [opts]
     * @returns {Promise<string|null>}
     */
    ensureToken: function (form, opts) {
      const container = form
        ? form.querySelector('.bfwt-turnstile-container')
        : null;
      if (!container) return Promise.resolve(null);
      container.removeAttribute('data-lazy-focus');

      const existing = form.querySelector(
        'input[name="cf-turnstile-response"]'
      );
      if (existing && existing.value) return Promise.resolve(existing.value);

      const timeoutMs = (opts && opts.timeoutMs) || 15000;

      return new Promise((resolve, reject) => {
        container._bfwtTokenResolvers = container._bfwtTokenResolvers || [];

        const onToken = (token) => {
          clearTimeout(timer);
          if (!token) reject(new Error('Turnstile verification failed'));
          else resolve(token);
        };
        container._bfwtTokenResolvers.push(onToken);

        const timer = setTimeout(() => {
          container._bfwtTokenResolvers = (
            container._bfwtTokenResolvers || []
          ).filter((r) => r !== onToken);
          reject(new Error('Turnstile timed out'));
        }, timeoutMs);

        // If the script hasn't loaded yet, kick it off
        if (typeof window.loadTurnstile === 'function') {
          window.loadTurnstile(true);
        }
        // If turnstile is already loaded, trigger explicit render
        if (typeof turnstile !== 'undefined' && !container.classList.contains('rendered')) {
          initializeTurnstile();
        }
      });
    },

    /**
     * Reset the widget so a fresh single-use token will be issued. Call
     * this in submit-error paths — the previous token is now spent and
     * server-side verification will reject any retry that reuses it.
     *
     * @param {HTMLFormElement} form
     */
    reset: function (form) {
      const container = form
        ? form.querySelector('.bfwt-turnstile-container')
        : null;
      if (!container) return;
      const input = form.querySelector('input[name="cf-turnstile-response"]');
      if (input) input.value = '';
      if (typeof turnstile !== 'undefined' && container._bfwtWidgetId) {
        try {
          turnstile.reset(container._bfwtWidgetId);
        } catch (e) {
          /* noop */
        }
      }
    },
  };

  window.initializeTurnstile = initializeTurnstile;

  window.loadTurnstile = function (force = false) {
    // Guard: Only load external Turnstile script if a visible container exists or explicitly forced
    const hasVisible = Array.from(document.querySelectorAll('.bfwt-turnstile-container:not([data-lazy-focus="1"])')).some(
      el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
    );
    if (!hasVisible && !force && typeof turnstile === 'undefined') {
      return;
    }
    if (typeof turnstile !== 'undefined') {
      initializeTurnstile();
      return;
    }
    if (
      document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      )
    )
      {return;}

    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=initializeTurnstile';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  };

  function initLazyTurnstile() {
    const triggerEvents = ['pointerenter', 'focusin', 'touchstart'];
    const onUserIntent = () => {
      triggerEvents.forEach((ev) =>
        document.removeEventListener(ev, onUserIntent)
      );
      window.loadTurnstile();
    };
    triggerEvents.forEach((ev) =>
      document.addEventListener(ev, onUserIntent, { passive: true, once: true })
    );

    // Fallback: load when browser is completely idle after page load
    const scheduleIdle = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => window.loadTurnstile(), { timeout: 5000 });
      } else {
        setTimeout(window.loadTurnstile, 3500);
      }
    };
    if (document.readyState === 'complete') {
      scheduleIdle();
    } else {
      window.addEventListener('load', scheduleIdle, { once: true });
    }
  }

  function checkAndInitTurnstile() {
    // Only arm lazy triggers if there is a VISIBLE Turnstile container on the page
    const hasVisible = Array.from(document.querySelectorAll('.bfwt-turnstile-container:not([data-lazy-focus="1"])')).some(
      el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
    );

    if (hasVisible) {
      initLazyTurnstile();
    } else {
      // Containers are in hidden modals/sheets; listen for modal open or on-demand mount
      const onModalOrMount = () => {
        setTimeout(() => {
          if (typeof window.loadTurnstile === 'function') {
            window.loadTurnstile(true);
          }
        }, 150);
      };
      document.addEventListener('bfwt:modal:opened', onModalOrMount);
      document.addEventListener('bfwt:sheet:opened', onModalOrMount);
      document.addEventListener('bfwt-turnstile-mount', onModalOrMount);
    }
  }

  // Warm up lazy-focus inline Turnstile containers as soon as user interacts with their parent form
  document.addEventListener(
    'focusin',
    function (e) {
      const form = e.target && e.target.closest ? e.target.closest('form') : null;
      if (!form) return;
      const lazyContainer = form.querySelector('.bfwt-turnstile-container[data-lazy-focus="1"]');
      if (lazyContainer) {
        lazyContainer.removeAttribute('data-lazy-focus');
        if (typeof window.loadTurnstile === 'function') {
          window.loadTurnstile(true);
        }
      }
    },
    { passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAndInitTurnstile);
  } else {
    checkAndInitTurnstile();
  }
})();
